import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Send, Check, UserPlus, Copy } from "lucide-react";
import { onb } from "@/lib/onb";
import { errText, copyText } from "@/lib/http";
import { Panel, Eyebrow } from "@/components/mh/bits";
import { Btn } from "@/components/mh/controls";

const DOT = { done: "#D4FF3A", in_progress: "#00E5FF", not_started: "#3A3A46" };
const LABEL = { done: "Done", in_progress: "In progress", not_started: "Not started" };

const Board = ({ iv, active, setActive, isOwner, reload }) => {
  const [assigning, setAssigning] = useState(null);
  const [email, setEmail] = useState("");
  const rows = [{ ...iv.company, slug: "company", name: "Company (owner)", hex: "#FFFFFF" }, ...iv.departments];
  const assign = async (slug) => {
    try {
      const r = (await onb.assign(slug, email)).data;
      if (r.invite_token) { copyText(`${window.location.origin}/invite/${r.invite_token}`); toast.success("Invite link copied. Send it to them"); } else toast.success("Assigned");
      setAssigning(null); setEmail(""); reload();
    } catch (e) { toast.error(errText(e)); }
  };
  return (
    <Panel cls="p-4 space-y-1" testId="onb-board">
      <Eyebrow cls="px-1 mb-2">Interview board</Eyebrow>
      {rows.map((r) => (
        <div key={r.slug} className={`rounded-lg px-3 py-2.5 transition-colors ${active === r.slug ? "bg-white/[0.06]" : "hover:bg-white/[0.03]"}`}>
          <button onClick={() => setActive(r.slug)} className="w-full flex items-center gap-2.5 text-left" data-testid={`onb-board-${r.slug}`}>
            <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: DOT[r.status] }} />
            <span className="flex-1 min-w-0"><span className="text-sm block truncate">{r.name}</span>
              <span className="font-mono text-[10px] text-muted-foreground truncate block">{LABEL[r.status]}{r.owner_email ? ` · ${r.owner_email}` : ""}</span></span>
          </button>
          {isOwner && r.slug !== "company" && r.status !== "done" && (assigning === r.slug ? (
            <div className="flex gap-1.5 mt-2">
              <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="head@company.com" data-testid={`onb-assign-email-${r.slug}`}
                className="flex-1 min-w-0 h-8 px-2.5 rounded-md bg-white/[0.03] border border-white/10 text-xs outline-none focus:border-white/30" />
              <button onClick={() => assign(r.slug)} className="h-8 px-2.5 rounded-md bg-[#D4FF3A] text-black text-xs font-semibold" data-testid={`onb-assign-save-${r.slug}`}>Invite</button>
            </div>
          ) : (
            <button onClick={() => setAssigning(r.slug)} className="mt-1 ml-4 inline-flex items-center gap-1 font-mono text-[10px] text-muted-foreground hover:text-foreground" data-testid={`onb-assign-${r.slug}`}>
              <UserPlus className="w-3 h-3" />{r.owner_email ? "Reassign" : "Assign a head"}</button>
          ))}
          {r.invite_token && r.status !== "done" && <button onClick={() => { copyText(`${window.location.origin}/invite/${r.invite_token}`); toast.success("Invite link copied"); }}
            className="mt-1 ml-4 inline-flex items-center gap-1 font-mono text-[10px] text-[#00E5FF]" data-testid={`onb-copy-invite-${r.slug}`}><Copy className="w-3 h-3" />Copy invite link</button>}
        </div>
      ))}
    </Panel>
  );
};

const Chat = ({ section, title, canEdit, onChange }) => {
  const [iv, setIv] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const end = useRef();
  useEffect(() => { setIv(null); onb.interview(section).then((r) => setIv(r.data)).catch(() => {}); }, [section]);
  useEffect(() => { end.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }); }, [iv, busy]);
  const send = async (t) => {
    setBusy(true);
    try { const r = (await onb.say(section, t)).data; setIv(r); setText(""); if (Object.keys(r.new_facts || {}).length) toast.success(`Learned: ${Object.keys(r.new_facts).join(", ")}`); onChange(); }
    catch (e) { toast.error(errText(e)); }
    setBusy(false);
  };
  const finish = async () => { try { await onb.finish(section); toast.success(`${title} interview marked done`); setIv((x) => ({ ...x, status: "done" })); onChange(); } catch (e) { toast.error(errText(e)); } };
  if (!iv) return <Panel cls="p-6 font-mono text-xs text-muted-foreground">Loading interview…</Panel>;
  return (
    <Panel cls="p-0 flex flex-col min-h-[520px] max-h-[72vh]" testId="onb-chat">
      <div className="px-5 py-4 border-b border-white/[0.06] flex flex-wrap items-center justify-between gap-2">
        <div><Eyebrow>Interview</Eyebrow><h3 className="font-display text-2xl">{title}</h3></div>
        <div className="flex items-center gap-2">
          <span className="font-mono text-[10px] uppercase" style={{ color: DOT[iv.status] }} data-testid="onb-chat-status">{LABEL[iv.status]}</span>
          {canEdit && iv.status !== "done" && iv.messages.length > 0 && <Btn onClick={finish} testId="onb-finish-btn"><Check className="w-4 h-4" />Mark done</Btn>}
        </div>
      </div>
      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3" data-testid="onb-messages">
        {iv.messages.length === 0 && (
          <div className="text-sm text-muted-foreground">About {section === "company" ? "10" : "5–10"} minutes. You can pause any time; it picks up where you left off.
            {canEdit && <div className="mt-3"><Btn variant="primary" onClick={() => send("")} disabled={busy} testId="onb-start-btn">{busy ? "Starting…" : "Start interview"}</Btn></div>}</div>
        )}
        {iv.messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "ai" ? "" : "justify-end"} mh-enter`}>
            <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm ${m.role === "ai" ? "bg-white/[0.04] border border-white/[0.06]" : "bg-[#D4FF3A]/[0.12] border border-[#D4FF3A]/25"}`} data-testid={`onb-msg-${i}`}>
              {m.text}{m.by && <div className="font-mono text-[9px] text-muted-foreground mt-1">{m.by}</div>}</div>
          </div>
        ))}
        {busy && iv.messages.length > 0 && <div className="font-mono text-[11px] text-muted-foreground">thinking…</div>}
        <div ref={end} />
      </div>
      {canEdit && iv.messages.length > 0 && (
        <form onSubmit={(e) => { e.preventDefault(); text.trim() && send(text); }} className="p-3 border-t border-white/[0.06] flex gap-2">
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={2} placeholder="Type your answer…" data-testid="onb-answer-input"
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); text.trim() && send(text); } }}
            className="flex-1 resize-none rounded-xl bg-white/[0.03] border border-white/10 px-3.5 py-2.5 text-sm outline-none focus:border-[#D4FF3A]/60" />
          <button type="submit" disabled={busy || !text.trim()} data-testid="onb-send-btn" className="w-11 rounded-xl bg-[#D4FF3A] text-black grid place-items-center disabled:opacity-40"><Send className="w-4 h-4" /></button>
        </form>
      )}
    </Panel>
  );
};

export const Interviews = ({ st, reload, canEdit, isOwner }) => {
  const iv = st.interviews;
  const mine = iv.departments.find((d) => d.owner_email === st.me && d.status !== "done");
  const [active, setActive] = useState(isOwner ? "company" : mine?.slug || "company");
  const title = active === "company" ? "Company" : iv.departments.find((d) => d.slug === active)?.name;
  return (
    <div className="grid lg:grid-cols-[280px_1fr] gap-4" data-testid="onb-interviews">
      <Board iv={iv} active={active} setActive={setActive} isOwner={isOwner} reload={reload} />
      <Chat key={active} section={active} title={title} canEdit={canEdit} onChange={reload} />
    </div>
  );
};
